/**
 * The People group: not its own source, but senders, recipients and event
 * participants pulled out of the *same query*'s Mail and Calendar results
 * (agora#216's 2026-09-21 comment) -- so it costs no request of its own, and
 * a mail-only or calendar-only agora still gets a People group from whatever
 * it has.
 *
 * A participant is included only when their own name or email contains the
 * query, not merely because they sit on a thread or event the query
 * matched: searching "Ada" should not surface every stranger cc'd on a
 * thread Ada happens to be mentioned in.
 *
 * Chat's own people are not built here: `chat.ts`'s `chatPeopleRows` reads
 * `ChatClient.people()` instead of a thread or event, and `model.ts`
 * concatenates the two lists into one People group. The key spaces never
 * collide -- this file keys a person by their lowercased email,
 * `chatPeopleRows` by their Matrix user id -- so neither needs to know
 * about the other (agora#268).
 *
 * **Where a person's row opens, and why.** `MailLocation` has no field for
 * "Mail, filtered to this person" -- it is `mailboxId`/`threadId`/`compose`
 * only (`routes.ts`) -- and `MailFilter`'s `from`/`to` are separately typed,
 * ANDed conditions with no way to ask for "this person as either" in one
 * query (`@wtfalch/email/mailbox`'s own `types.ts`), so there is no single
 * request that would answer it either. Mail cannot filter to a person today;
 * a person's row opens their most recent thread from *this* query's own Mail
 * results instead, which needs no extra request. Someone found only through
 * a Calendar participant, with no thread in this query's Mail results, opens
 * their event instead.
 */

import type { CalendarEvent } from '@wtfalch/calendar/calendar';
import type { EmailAddress, ThreadSummary } from '@wtfalch/email/mailbox';
import type { AgoraRoute } from '../routes.ts';
import type { SearchRow } from './types.ts';

function contains(haystack: string, query: string): boolean {
  return haystack.toLowerCase().includes(query.toLowerCase());
}

function matches(name: string | null | undefined, email: string, query: string): boolean {
  return contains(email, query) || (!!name && contains(name, query));
}

type PersonEntry = {
  name: string | null;
  email: string;
  time: string;
  route: AgoraRoute;
  /** Mail beats Calendar: a thread is a real conversation with this person,
   *  an event invite is not. Once a mail thread has been recorded for an
   *  email, a later, even more recent, calendar event never displaces it --
   *  it only fills in a name the thread's own address lacked. */
  fromMail: boolean;
};

function considerAddress(
  entry: Map<string, PersonEntry>,
  address: EmailAddress,
  query: string,
  time: string,
  route: AgoraRoute,
  fromMail: boolean,
): void {
  const email = address.email?.trim();
  if (!email) return;
  const name = address.name?.trim() || null;
  if (!matches(name, email, query)) return;

  const key = email.toLowerCase();
  const existing = entry.get(key);
  if (!existing) {
    entry.set(key, { name, email, time, route, fromMail });
    return;
  }
  if (fromMail && !existing.fromMail) {
    entry.set(key, { name: name ?? existing.name, email, time, route, fromMail: true });
    return;
  }
  if (fromMail === existing.fromMail && new Date(time) > new Date(existing.time)) {
    entry.set(key, { name: name ?? existing.name, email, time, route, fromMail });
    return;
  }
  if (!existing.name && name) entry.set(key, { ...existing, name });
}

export function peopleRows(
  agoraId: string,
  query: string,
  threads: readonly ThreadSummary[],
  events: readonly CalendarEvent[],
): readonly SearchRow[] {
  const byEmail = new Map<string, PersonEntry>();

  for (const thread of threads) {
    const route: AgoraRoute = {
      kind: 'agora',
      agoraId,
      section: 'mail',
      location: { mailboxId: thread.mailboxIds[0] ?? null, threadId: thread.id },
    };
    const addresses = [...(thread.from ? [thread.from] : []), ...thread.to, ...thread.participants];
    for (const address of addresses) {
      considerAddress(byEmail, address, query, thread.receivedAt, route, true);
    }
  }

  for (const event of events) {
    const route: AgoraRoute = {
      kind: 'agora',
      agoraId,
      section: 'calendar',
      location: { date: event.start.slice(0, 10), eventId: event.id },
    };
    for (const participant of event.participants ?? []) {
      if (!participant.calendarAddress) continue;
      considerAddress(
        byEmail,
        { name: participant.name, email: participant.calendarAddress },
        query,
        event.start,
        route,
        false,
      );
    }
  }

  return [...byEmail.values()]
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    .map((person) => ({
      id: person.email.toLowerCase(),
      title: person.name ?? person.email,
      snippet: person.name ? person.email : undefined,
      time: person.time,
      route: person.route,
    }));
}
